import React, {useEffect, useReducer, useRef, useState} from 'react';
import {ContentAdDesktop} from 'app/styles/content';
import {CompetitionCaptionDouble, Subheading} from 'app/styles/heading';
import {
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemIcon,
  ListItemSecondaryAction,
  ListItemText,
  Switch,
} from '@mui/material';
import {LinkOnComponent} from 'app/components/links/link';
import {Button, COLORS, Image, Placeholder, SIZES} from '@fupa/fupa-uikit';
import {useDispatch, useSelector} from 'react-redux';
import {selectCurrentUser, selectFollowedEntities} from 'app/hooks/reduxCreateSelectorHooks';
import {Add, Close, Notifications, NotificationsOff} from '@mui/icons-material';
import {updateAllPushes, updateFollowEntity, updatePushEntity} from 'app/services/follow';
import {updateSnackbarInfo} from 'app/components/snackbar/SnackbarActions';
import {pushError, pushStatus} from 'app/components/snackbar/SnackbarContent';
import {fetchFollows} from 'app/actions/currentUserActions';
import styled from 'styled-components';
import {DeviceManagementLinkRow} from 'app/components/settings/deviceManagementLinkRow';
import {selectFollowInfo, FOLLOWABLE_SEARCH_ENTITIES} from 'app/components/follow/followHelper';
import {changeHeaderStyle, changeHeaderText} from 'app/actions/headerActions';
import {DeleteFollowingPrompt} from 'app/components/follow/DeleteFollowingPrompt';
import {Helmet} from 'react-helmet-async';
import {TITLE_POSTFIX} from 'app/seo/seo.constants';
import {useHistory} from 'react-router';
import {PushBlockedAlert} from 'app/components/banner/PushBlockedAlert';
import {MatchRow} from 'app/components/match/matchRow/MatchRow';
import {formatDateWithWeekday} from 'app/helpers/dateHelpers';
import {useNative} from 'app/contexts/NativeContext';

const CustomSubheading = styled(Subheading)`
  &&& {
    margin: 0;
  }
`;

const MatchContainer = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  gap: 0 1rem;
  padding: 0 1rem;
  ${props =>
    props.theme.desktopLayout`
    padding: 0
  `}
`;

const SCButton = styled(Button)`
  margin: ${SIZES[8]} 0;
`;

const init = listOfFollowedEntites => {
  const pushes = listOfFollowedEntites.map(entity => {
    return {[entity.id]: {push: entity.push}};
  });
  return pushes.reduce((result, item) => Object.assign(result, item), {});
};

function reducer(state, action) {
  switch (action.type) {
    case 'TOGGLE_PUSH':
      return {...state, [action.key]: {push: action.push}};
    case 'INIT_PUSH': {
      return init(action.data);
    }
    default:
      return state;
  }
}

const FollowingSettingsPage = () => {
  const followedEntities = useSelector(selectFollowedEntities);
  const {followsError, notificationPermission} = useSelector(selectCurrentUser);
  const listOfPushedMatches = followedEntities.filter(entity => entity.entity === 'match');
  // Use component state pushedMatchesUI to show optimistic ui for deleting matches triggered by global toggle button
  const [pushedMatchesUI, setPushedMatchesUI] = useState(listOfPushedMatches);
  const listOfFollowedEntites = followedEntities.filter(entity => entity.entity !== 'match');
  const dispatchRedux = useDispatch();
  const [state, dispatch] = useReducer(reducer, followedEntities, init);
  const [switchChecked, setSwitchChecked] = useState(!!Object.keys(state).find(entity => state[entity].push));
  const [open, setOpen] = useState(false);
  const deleteEntity = useRef(null);
  const history = useHistory();

  const [pushBlockedOpen, setPushBlockedOpen] = useState(false);
  const {isNativeApp} = useNative();

  useEffect(() => {
    dispatchRedux(changeHeaderStyle({size: 'small'}));
    dispatchRedux(changeHeaderText({title: 'Favoriten'}));
  }, []);

  useEffect(() => {
    return () => {
      // sync push updates of component state to redux
      dispatchRedux(fetchFollows());
    };
  }, []);

  useEffect(() => {
    setPushedMatchesUI(listOfPushedMatches);
    // Delete matches on deactivate push for all entites by global toggle button
    if (listOfPushedMatches.length && listOfPushedMatches.find(match => !match.push)) {
      const deleteMatches = [];
      setPushedMatchesUI([]);
      listOfPushedMatches.map(match => {
        if (!match.push) {
          deleteMatches.push(updateFollowEntity(match.entity, match.id, true));
        }
      });
      Promise.all(deleteMatches).then(() => dispatchRedux(fetchFollows()));
    }
  }, [followedEntities]);

  useEffect(() => {
    setSwitchChecked(!!Object.keys(state).find(entity => state[entity].push));
  }, [state]);

  const togglePush = entity => {
    const pushState = !state[entity.id]?.push;
    if (pushState && isNativeApp && notificationPermission === 'denied') {
      setPushBlockedOpen(true);
    } else {
      dispatch({type: 'TOGGLE_PUSH', key: entity.id, push: pushState ?? !entity.push});
      updatePushEntity(entity.entity, entity.id, pushState ?? !entity.push)
        .then(() => {
          const status = pushState ? 'aktiviert' : 'deaktiviert';
          dispatchRedux(updateSnackbarInfo(pushStatus(status, true)));
        })
        .catch(() => {
          dispatchRedux(updateSnackbarInfo(pushError));
        });
    }
  };

  const deleteFollowing = (event, entity) => {
    event.preventDefault();
    deleteEntity.current = entity;
    setOpen(true);
  };

  const handleResolve = entity => {
    updateFollowEntity(entity.entity, entity.id, true).then(() => dispatchRedux(fetchFollows()));
    deleteEntity.current = null;
    setOpen(false);
  };

  const handleReject = () => {
    deleteEntity.current = null;
    setOpen(false);
  };

  const handleAddFavorites = () => {
    history.push('/search', {filters: FOLLOWABLE_SEARCH_ENTITIES});
  };

  const elements = listOfFollowedEntites.length
    ? listOfFollowedEntites.map(entity => {
        const {imageType, path, name} = selectFollowInfo[entity.entity](entity);

        const icon = state?.[entity.id]?.push ? (
          <Notifications color={'secondary'} />
        ) : (
          <NotificationsOff color={'action'} />
        );
        return (
          <ListItem
            key={entity.id}
            sx={{display: 'flex!important'}}
            component={LinkOnComponent}
            to={`/${path}/${entity.slug}`}
          >
            <ListItemIcon>
              <IconButton onClick={e => deleteFollowing(e, entity)} size='large'>
                <Close color={'action'} fontSize='small' />
              </IconButton>
            </ListItemIcon>
            <ListItemAvatar>
              <Image entity={imageType} width={32} height={32} ratio='1:1' circle={true} src={entity.image} />
            </ListItemAvatar>
            <ListItemText primary={name} />
            <ListItemSecondaryAction>
              <IconButton edge='end' onClick={() => togglePush(entity)} size='large'>
                {icon}
              </IconButton>
            </ListItemSecondaryAction>
          </ListItem>
        );
      })
    : null;

  const showPlaceholder = listOfFollowedEntites.length ? null : (
    <Placeholder>Du hast noch keine Favoriten hinzugefügt.</Placeholder>
  );

  const handleToggleAllPushes = () => {
    const pushOption = switchChecked ? 'none' : 'all';
    if (pushOption === 'all' && isNativeApp && notificationPermission === 'denied') {
      setPushBlockedOpen(true);
    } else {
      Object.keys(state).map(entity => {
        dispatch({type: 'TOGGLE_PUSH', key: entity, push: !switchChecked});
      });
      setSwitchChecked(!switchChecked);
      updateAllPushes(pushOption)
        .then(() => {
          const status = pushOption === 'all' ? 'aktiviert' : 'deaktiviert';
          dispatchRedux(updateSnackbarInfo(pushStatus(status, true)));
          dispatchRedux(fetchFollows());
        })
        .catch(() => {
          dispatch({type: 'INIT_PUSH', data: listOfFollowedEntites});
          dispatchRedux(updateSnackbarInfo(pushError));
        });
    }
  };

  const matches = pushedMatchesUI.length ? (
    pushedMatchesUI.map(match => (
      <React.Fragment key={match.slug}>
        <MatchRow
          match={match}
          caption={
            <CompetitionCaptionDouble variant='caption1'>
              <span>{match.competition.middleName}</span>
              <span>{formatDateWithWeekday(match.kickoff)}</span>
            </CompetitionCaptionDouble>
          }
          colored={false}
          dynamicDateformat={false}
        />
      </React.Fragment>
    ))
  ) : (
    <Placeholder>Du hast keine Spiele gepusht.</Placeholder>
  );

  const renderDeleteFollowingPrompt = deleteEntity.current ? (
    <DeleteFollowingPrompt
      entity={deleteEntity.current}
      open={open}
      onReject={handleReject}
      onResolve={handleResolve}
    />
  ) : null;

  const content = followsError ? (
    <Placeholder>Beim Laden deiner Favoriten ist ein Fehler aufgetreten. Versuche es später erneut.</Placeholder>
  ) : (
    <>
      <CustomSubheading variant='subheading2'>Allgemein</CustomSubheading>
      <List>
        <DeviceManagementLinkRow />
        <ListItem>
          <ListItemText
            id='switch-push-notifications'
            primary='Push-Benachrichtigungen'
            secondary='Alle Favoriten und Spiele de-/aktivieren'
          />
          <ListItemSecondaryAction>
            <Switch
              edge='end'
              onChange={handleToggleAllPushes}
              checked={switchChecked}
              inputProps={{'aria-labelledby': 'switch-push-notifications'}}
            />
          </ListItemSecondaryAction>
        </ListItem>
      </List>
      {renderDeleteFollowingPrompt}
      <CustomSubheading variant='subheading2'>Favoriten</CustomSubheading>
      <List>
        {showPlaceholder}
        <SCButton
          label='Favoriten hinzufügen'
          justify='center'
          icon={<Add style={{color: COLORS.white}} />}
          onClick={handleAddFavorites}
        />
        {elements}
      </List>
      <CustomSubheading variant='subheading2'>Spiele</CustomSubheading>
      <MatchContainer>{matches}</MatchContainer>
      <PushBlockedAlert open={isNativeApp ? pushBlockedOpen : false} onClose={() => setPushBlockedOpen(false)} />
    </>
  );

  return (
    <ContentAdDesktop noTabs top='4.5rem'>
      <Helmet title={`Favoriten${TITLE_POSTFIX}`} />
      {content}
    </ContentAdDesktop>
  );
};

export default FollowingSettingsPage;
